import { getPeriodDates, type Cohort } from 'Cohorts';
import { type Period } from 'Period';
import { MIME_TYPE_PDF, MIME_TYPE_XLSX, MIME_TYPE_ZIP } from 'ProjectSubmission/constants';
import { type LearnerProject, type SubmissionFormatType } from './LearnerProjects.types';
import { SUBMISSION_FORMAT_SPREADSHEET } from './constants';

// Resolve a period's LearnerProject objects from the cohort-level `learnerProjects` array via the
// period's `learnerProjectIds`. Lets consumers stop reading the per-period `learnerProjects` array
// (duplicated on every cohort payload) in preparation for its removal. See
// https://trello.com/c/euiBR0PP.
export const getPeriodLearnerProjects = (cohort: Cohort, period: Period): LearnerProject[] =>
    // `cohort.learnerProjects ?? []` guards against a cached/stale cohort payload that predates the
    // top-level learner_projects field (e.g. mid-deploy version skew) — without it this throws.
    (period.learnerProjectIds ?? [])
        .map(id => (cohort.learnerProjects ?? []).find(lp => lp.id === id))
        .filter((lp): lp is LearnerProject => !!lp);

// Map submission format types to MIME types (only for file-upload formats)
const SUBMISSION_FORMAT_TO_MIME_TYPE: Partial<Record<SubmissionFormatType, string>> = {
    spreadsheet: MIME_TYPE_XLSX,
    pdf: MIME_TYPE_PDF,
    zip: MIME_TYPE_ZIP,
};

export const isCapstoneIdentifier = (identifier: string) => ['capstone', 'isdp'].some(v => identifier.match(v));
export const isCapstoneProject = (project: LearnerProject) => isCapstoneIdentifier(project.requirementIdentifier);

const PassingScores = {
    capstone: 3,
    standard: 2,
    presentation: 2,
} as const;

export const getPassingProjectScore = (cohort: Cohort | null, projectType: string) => {
    if (
        projectType === 'standard' &&
        cohort &&
        ((cohort.programType === 'emba' && new Date(cohort.startDate * 1000) <= new Date('2019/07/01')) || // EMBA18 start date
            (cohort.programType === 'mba' && new Date(cohort.startDate * 1000) <= new Date('2019/07/29'))) // MBA23 start date
    ) {
        return 1;
    }

    return PassingScores[projectType as keyof typeof PassingScores];
};

export const getProjectDates = (cohort: Cohort | null, project: LearnerProject, projectStartPeriodIndex: number) => {
    if (!cohort) return { startDate: null, endDate: null };

    // findLastIndex can return -1 if it doesn't find an entry matching the predicate, in which case getPeriodDates would throw an error at runtime.
    // This should only ever be a theoretical issue for us because, as of writing, the only way to add a LearnerProject to a Cohort's curriculum is to
    // add it to a period or periods.
    const lastPeriodWithProjectIndex = cohort.periods.findLastIndex(p =>
        (p.learnerProjectIds ?? []).includes(project.id),
    );

    const { startDate } = getPeriodDates(cohort, projectStartPeriodIndex);
    const { endDate } = getPeriodDates(cohort, lastPeriodWithProjectIndex);

    return { startDate, endDate };
};

/**
 * Determines if a project is a spreadsheet-only project that expects XLSX submissions.
 * Used to show spreadsheet-specific guidance (e.g., Google Sheets export instructions)
 * in the submission form.
 *
 * Returns true only when the project expects exactly one submission format and it's a spreadsheet.
 * This ensures we don't show spreadsheet guidance for projects that support multiple formats.
 */
export const isSpreadsheetOnlyProject = (project: LearnerProject): boolean => {
    if (!project.submissionFormats || project.submissionFormats.length === 0) {
        return false;
    }

    // Only return true if there's exactly one format and it's spreadsheet
    return (
        project.submissionFormats.length === 1 && project.submissionFormats[0].type === SUBMISSION_FORMAT_SPREADSHEET
    );
};

/**
 * Returns the accepted MIME types for file uploads based on the project's submissionFormats.
 *
 * When submissionFormats is configured, returns only the MIME types that correspond to
 * file-upload formats (spreadsheet → xlsx, pdf → pdf). Link-only formats are ignored.
 *
 * Falls back to accepting both PDF and XLSX when:
 * - submissionFormats is not configured (null/undefined/empty)
 * - submissionFormats contains only link types (no file-upload formats)
 *
 * @returns Comma-separated string of MIME types suitable for the FileInput accept prop
 */
export const getAcceptedFileTypesForProject = (project: LearnerProject): string => {
    const defaultTypes = [MIME_TYPE_PDF, MIME_TYPE_XLSX].join(',');

    if (!project.submissionFormats || project.submissionFormats.length === 0) {
        return defaultTypes;
    }

    const mimeTypes = project.submissionFormats
        .map(format => SUBMISSION_FORMAT_TO_MIME_TYPE[format.type])
        .filter((mimeType): mimeType is string => !!mimeType);

    // If no file-upload formats configured (only link types), fall back to defaults
    if (mimeTypes.length === 0) {
        return defaultTypes;
    }

    return mimeTypes.join(',');
};
